import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useLang } from "../../context/LanguageContext";
import { images } from "../../lib/api";
import ChaletReel from "./ChaletReel";

export default function Hero() {
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [idx, setIdx] = useState(0);
  const [reelOpen, setReelOpen] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % images.hero.length), 6500);
    return () => clearInterval(id);
  }, []);

  const scrollDown = () => {
    const el = document.getElementById("booking");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section data-testid="hero-section" className="relative min-h-[100svh] w-full overflow-hidden hero-grain">
      {/* Slideshow layer — Ken-Burns slow zoom, crossfade */}
      {images.hero.map((src, i) => (
        <img
          key={src}
          src={src}
          alt="Chalet Steiermark"
          loading={i === 0 ? "eager" : "lazy"}
          className={`hero-slide ${i === idx ? "is-active" : ""}`}
        />
      ))}

      {/* Lighter gradient — shows more image, text stays readable via text-shadow */}
      <div
        className="absolute inset-0 z-[2]"
        style={{
          background:
            "linear-gradient(95deg, hsla(158,50%,6%,0.82) 0%, hsla(158,45%,8%,0.55) 30%, hsla(158,40%,12%,0.22) 55%, transparent 75%)",
        }}
      />
      {/* Softer vignette + bottom fade */}
      <div
        className="absolute inset-0 z-[2]"
        style={{
          background:
            "radial-gradient(ellipse at 30% 50%, transparent 0%, hsla(158,50%,4%,0.22) 80%), linear-gradient(180deg, hsla(158,50%,4%,0.35) 0%, transparent 22%, transparent 68%, hsl(var(--background)) 100%)",
        }}
      />

      <div className="relative z-10 container-x min-h-[100svh] flex flex-col justify-center pt-32 pb-32">
        <div className="max-w-3xl text-white">
          <div className="overline !text-[hsl(var(--accent))] fade-up flex items-center gap-3">
            <span className="inline-block h-px w-10 bg-[hsl(var(--accent))]"></span>
            {t.hero.eyebrow}
          </div>
          <h1
            data-testid="hero-title"
            className="font-serif font-light text-5xl sm:text-6xl md:text-7xl lg:text-[7.5rem] leading-[0.92] tracking-[-0.01em] mt-6 whitespace-pre-line fade-up delay-100"
            style={{ textShadow: "0 2px 30px rgba(0,0,0,0.6), 0 0 80px rgba(0,0,0,0.3)" }}
          >
            {t.hero.title}
          </h1>
          <p className="mt-8 max-w-xl text-base md:text-lg text-white leading-relaxed fade-up delay-200 font-light"
             style={{ textShadow: "0 1px 12px rgba(0,0,0,0.5)" }}>
            {t.hero.sub}
          </p>
          <div className="mt-10 flex flex-wrap gap-4 fade-up delay-300">
            <button data-testid="hero-cta-book" onClick={() => nav("/book")} className="btn-primary !bg-[hsl(var(--accent))] !text-[hsl(var(--accent-foreground))] !border-[hsl(var(--accent))] hover:!bg-white hover:!text-[hsl(var(--primary))] hover:!border-white">
              {t.hero.cta}
              <span aria-hidden>→</span>
            </button>
            <button
              data-testid="hero-cta-reel"
              onClick={() => setReelOpen(true)}
              className="btn-ghost !text-white !border-white/60 hover:!bg-white hover:!text-[hsl(var(--primary))] group"
              aria-label={lang === "de" ? "Werbefilm ansehen" : "Watch promo film"}
            >
              <span className="inline-flex items-center justify-center w-5 h-5 border border-current rounded-full group-hover:bg-[hsl(var(--accent))] group-hover:border-[hsl(var(--accent))] group-hover:text-white transition-colors">
                <span className="block w-0 h-0 border-y-[5px] border-y-transparent border-l-[7px] border-l-current ml-0.5" />
              </span>
              {lang === "de" ? "Werbefilm" : "Promo film"}
            </button>
            <button data-testid="hero-scroll" onClick={scrollDown} className="btn-ghost !text-white !border-white/60 hover:!bg-white hover:!text-[hsl(var(--primary))]">
              {t.hero.scroll}
            </button>
          </div>

          {/* Combo banner */}
          <button
            data-testid="hero-combo-banner"
            onClick={() => nav("/book?unit=combo")}
            className="group mt-12 inline-flex items-center gap-4 bg-black/35 backdrop-blur-md border border-white/30 hover:bg-white/95 hover:text-[hsl(var(--primary))] hover:border-white transition-all px-5 py-4 text-left fade-up delay-400"
          >
            <span className="inline-flex items-center justify-center w-10 h-10 bg-[hsl(var(--accent))] text-white font-serif text-base shrink-0">−10%</span>
            <span className="flex-1">
              <span className="block text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--accent))] font-semibold">Combo</span>
              <span className="block text-sm md:text-base mt-0.5">{t.hero.combo_banner.replace('Combo · ', '')}</span>
            </span>
            <span aria-hidden className="opacity-80 group-hover:translate-x-1 transition-transform">→</span>
          </button>
        </div>
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-8 right-8 z-10 hidden md:flex items-center gap-3">
        {images.hero.map((_, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            aria-label={`Slide ${i + 1}`}
            data-testid={`hero-indicator-${i}`}
            className={`h-px transition-all ${i === idx ? "w-12 bg-[hsl(var(--accent))]" : "w-6 bg-white/40 hover:bg-white/80"}`}
          />
        ))}
      </div>

      <ChaletReel open={reelOpen} onClose={() => setReelOpen(false)} />
    </section>
  );
}
